// src/components/projects/ChatWindow/ConfirmDisconnectDialog.js

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  alpha,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  WarningOutlined,
  Delete,
  Close,
} from '@mui/icons-material';

const ConfirmDisconnectDialog = ({
  open,
  onClose,
  provider,
  onConfirm,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          maxWidth: 400,
        }
      }}
    >
      {/* Warning Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.main,
              flexShrink: 0,
            }}
          >
            <WarningOutlined sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
              Delete Configuration
            </Typography>
            <Typography variant="caption" color="textSecondary">
              This action cannot be undone
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.2),
              }
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Are you sure you want to delete the configuration for <strong>{provider}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
            This will permanently remove the API key and all associated settings. You will need to reconfigure the model to use it again.
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.warning.main, 0.06),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.75rem' }}>
              ⚠️ This will affect all users using this model configuration
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.divider, 0.1),
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          startIcon={<Delete />}
          sx={{
            px: 3,
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: theme.palette.error.dark,
            }
          }}
        >
          Delete Config
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDisconnectDialog;